/**
 * Scene loader plugin for Trace scene documents.
 *
 * A scene document is a glTF 2.1 JSON file (`.gltf`, or `.glb` for a packaged
 * one) that composes other files: `files[]` is its dependency manifest and
 * `externalAssets[]` / `node.externalAsset` instantiate glTF sub-scenes. The
 * legacy glXF shape (`assets[]` / `node.asset`) reads through the same path —
 * `normalizeSceneDocument` is the single reader of both
 * (see `docs-internal/GLTF_2_1_MIGRATION_PLAN.md` D4).
 *
 * `.gltf` and `.glb` also name plain models, so the *extension* never routes a
 * file here: `TraceViewer.open` and `TraceScene._loadFromBlob` sniff the
 * content first (D2, `isSceneDocument`).
 *
 * Trace-extras awareness: when nodes carry `extras.trace.{behaviors,active}`
 * those payloads are forwarded onto the loaded result so a standalone document
 * round-trips behaviors the same way a `.tracex` archive does. A document-level
 * `KHR_interactivity` extension is also restored as a root-node BehaviorGraph.
 *
 * Transform semantic: PRESERVE
 * - Asset's internal transform is preserved
 * - Scene transform composes with asset transform (glTF behavior)
 */

import type {
    ISceneLoaderPluginAsync,
    ISceneLoaderAsyncResult,
    ISceneLoaderProgressEvent,
    ISceneLoaderPluginExtensions,
} from "@babylonjs/core/Loading/sceneLoader";
import type { IGLTFLoaderData } from "@babylonjs/loaders/glTF/glTFFileLoader";
import { GLTFLoaderAnimationStartMode } from "@babylonjs/loaders/glTF/glTFFileLoader.pure";
import { ImportMeshAsync } from "@babylonjs/core/Loading/sceneLoader";
import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import { normalizeSceneDocument, parseSceneDocumentJson } from '../formats/gltf21/normalize';
import {
    GLTF_BINARY_MIME,
    GLTF_JSON_MIME,
    type NormalizedNode,
    type NormalizedSceneDocument,
} from '../formats/gltf21/types';
import {
    foreignDocumentData,
    foreignNodeData,
    type ForeignDocumentData,
} from '../formats/gltf21/foreign';
import type { ParsedDocumentNode, ParsedDocumentStructure } from './parsedDocument';
import { migrateToDocumentRoot, type DocumentRootMigration } from './documentRootMigration';
import { isSceneFormatRef } from './sceneFormatExtensions';
import { bakedFromRig } from '../restoreBakedSrc';
import { matchAssetFileUrlPath } from '../../content/assetRef';
// Stamps every loaded glTF node with its file's TRACE_animation_binding names.
import '../gltf/TRACE_animation_binding';
import { Logger } from '../../utils/Logger';
import type { KHRInteractivityData } from '../../utils/ModelLoader';
import type { BehaviorGraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { convertKHRInteractivityToGraphData } from '../../behaviors/behaviorgraph/KHRInteractivityConverter';
import { recollapseSubgraphs } from '../../behaviors/behaviorgraph/subgraphRecollapse';
import type { ImportTranslationContext } from '../../behaviors/behaviorgraph/nodeIndexTranslation';
import {
    applyTransform,
    processRootNode,
    loadNodesWithResult as loadNodesWithResultUtil,
    loadNodesIntoContainer as loadNodesIntoContainerUtil,
    type GLXFLoadedNode,
    type GLXFLoadedResult,
} from './sceneLoadingUtils';

// =============================================================================
// Types
// =============================================================================

export type { ParsedDocumentNode, ParsedDocumentStructure } from './parsedDocument';

/**
 * Trace behaviors that own asset loading themselves — when present on a
 * node's `extras.trace.behaviors`, the inline load should be skipped.
 * `Model` consumes GLB, `Image` consumes a texture, `Video` consumes a
 * video, `AudioClip` an audio file. If a new asset-owning behavior is added,
 * list it here too.
 */
const TRACE_ASSET_OWNING_BEHAVIORS = new Set(['Model', 'Image', 'Video', 'AudioClip']);

/**
 * Whether a node's extras declare a Trace asset-owning behavior.
 * Used to suppress the inline GLB load for Trace experience nodes whose
 * behaviors will load the asset via ResourceManager instead.
 */
function nodeHasAssetOwningBehavior(extras: unknown): boolean {
    const behaviors = traceBehaviorsOf(extras);
    if (!behaviors) return false;
    return behaviors.some(b => typeof b['@type'] === 'string' && TRACE_ASSET_OWNING_BEHAVIORS.has(b['@type']));
}

/** The Trace behaviors a node's extras author, if any. */
function traceBehaviorsOf(extras: unknown): ReadonlyArray<{ '@type'?: string }> | undefined {
    const behaviors = (extras as { trace?: { behaviors?: unknown } } | undefined)?.trace?.behaviors;
    return Array.isArray(behaviors) ? behaviors as ReadonlyArray<{ '@type'?: string }> : undefined;
}

/** Absolute references pass through resolution untouched. */
function isAbsoluteUri(uri: string): boolean {
    return uri.startsWith('http://')
        || uri.startsWith('https://')
        || uri.startsWith('blob:')
        || uri.startsWith('data:');
}

// =============================================================================
// Plugin Implementation
// =============================================================================

export class GltfSceneLoaderPlugin implements ISceneLoaderPluginAsync {
    readonly name: string = "gltf-scene";
    readonly extensions: ISceneLoaderPluginExtensions = {
        ".glxf": { isBinary: false },
    };

    // Track blob URLs for cleanup
    private _blobUrls: string[] = [];

    // Store last loaded result for retrieval
    private _lastLoadedResult: GLXFLoadedResult | null = null;

    // Optional lookup for external asset blobs (companion files registered
    // alongside a single-file open). Checked before rootUrl resolution.
    private _externalAssetLookup: ((uri: string) => Blob | undefined) | null = null;

    // Whether this document is being opened AS the scene — see setOpenAsScene.
    private _openAsScene = false;

    /**
     * Provide a lookup for external asset references. Used when a standalone
     * document is opened from a blob together with its sibling files: the host
     * registers the siblings and this lookup resolves `files[].uri` to their
     * blobs instead of fetching `rootUrl + uri`.
     */
    setExternalAssetLookup(lookup: ((uri: string) => Blob | undefined) | null): void {
        this._externalAssetLookup = lookup;
    }

    /**
     * Say that this load is establishing the SCENE, not placing a reference.
     *
     * The only thing it changes is what happens to a FOREIGN multi-root
     * document (SCENE_ROOT_SPEC §2): opened as the scene it gets a synthesized
     * document root named after the file, placed it keeps its roots and the
     * placement is the handle. Our own documents are normalized either way.
     */
    setOpenAsScene(openAsScene: boolean): void {
        this._openAsScene = openAsScene;
    }

    // =========================================================================
    // ISceneLoaderPluginAsync Implementation
    // =========================================================================

    /**
     * Primary method - loads entire scene.
     */
    async loadAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<void> {
        this._blobUrls = [];
        this._lastLoadedResult = null;

        try {
            const structure = await this.parseSceneData(data, rootUrl, fileName);
            this._lastLoadedResult = await this.loadNodesWithResult(structure, scene, onProgress);
        } finally {
            this.cleanupBlobUrls();
        }
    }

    /**
     * Import meshes - delegates to loadAsync since we load entire scenes.
     *
     * Note: The meshesNames parameter is intentionally ignored. Scene composition
     * formats load complete scene graphs with hierarchies. Mesh filtering
     * would break parent-child relationships. Use loadAsync() for full scene loading.
     */
    async importMeshAsync(
        meshesNames: string | readonly string[] | null | undefined,
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<ISceneLoaderAsyncResult> {
        // Warn if caller expects mesh filtering (which we don't support)
        if (meshesNames !== null && meshesNames !== undefined) {
            Logger.warn(
                `${this.name}: meshesNames parameter is ignored. ` +
                `Scene composition formats load complete scene graphs.`
            );
        }
        const beforeMeshes = new Set(scene.meshes);
        const beforeAnimations = new Set(scene.animationGroups);
        const beforeSkeletons = new Set(scene.skeletons);
        const beforeTransforms = new Set(scene.transformNodes);
        const beforeGeometries = new Set(scene.geometries);
        const beforeLights = new Set(scene.lights);

        await this.loadAsync(scene, data, rootUrl, onProgress, fileName);

        return {
            meshes: scene.meshes.filter(m => !beforeMeshes.has(m)),
            animationGroups: scene.animationGroups.filter(a => !beforeAnimations.has(a)),
            skeletons: scene.skeletons.filter(s => !beforeSkeletons.has(s)),
            transformNodes: scene.transformNodes.filter(t => !beforeTransforms.has(t)),
            geometries: scene.geometries.filter(g => !beforeGeometries.has(g)),
            lights: scene.lights.filter(l => !beforeLights.has(l)),
            particleSystems: [],
            spriteManagers: [],
        };
    }

    /**
     * Load into an asset container.
     */
    async loadAssetContainerAsync(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (event: ISceneLoaderProgressEvent) => void,
        fileName?: string
    ): Promise<AssetContainer> {
        const container = new AssetContainer(scene);
        this._blobUrls = [];

        try {
            const structure = await this.parseSceneData(data, rootUrl, fileName);
            await this.loadNodesIntoContainer(structure, container, scene, onProgress);
        } finally {
            this.cleanupBlobUrls();
        }

        return container;
    }

    // =========================================================================
    // Public API for TraceScene Integration
    // =========================================================================

    /**
     * Get the result from the last load operation.
     * Call after loadAsync() to get the loaded result with node metadata.
     */
    getLastLoadedResult(): GLXFLoadedResult | null {
        return this._lastLoadedResult;
    }

    /**
     * Load a scene and return the loaded result directly.
     * This is the preferred method for TraceScene integration.
     */
    async loadWithResult(
        scene: Scene,
        data: unknown,
        rootUrl: string,
        onProgress?: (loaded: number, total: number) => void,
        fileName?: string
    ): Promise<GLXFLoadedResult> {
        this._blobUrls = [];

        try {
            const structure = await this.parseSceneData(data, rootUrl, fileName);
            const progressAdapter = onProgress
                ? (event: ISceneLoaderProgressEvent) => {
                    onProgress(event.loaded, event.total);
                }
                : undefined;
            return await this.loadNodesWithResult(structure, scene, progressAdapter);
        } finally {
            this.cleanupBlobUrls();
        }
    }

    // =========================================================================
    // Parsing Methods (can be overridden by subclasses)
    // =========================================================================

    /**
     * Parse scene-document JSON into the common scene structure.
     */
    protected async parseSceneData(
        data: unknown,
        rootUrl: string,
        fileName?: string
    ): Promise<ParsedDocumentStructure> {
        const document = this.parseDocument(data);
        const nodes = this.parseDocumentNodes(document, rootUrl);
        const migration = this.migrateDocumentRoot(document, nodes, fileName);
        this.restoreKHRInteractivity(document, nodes, migration);

        return {
            nodes,
            assetBlobUrls: new Map(),
            rootUrl,
            document,
            ...(migration.root ? { documentRoot: migration.root } : {}),
            ...(migration.scripts ? { scripts: migration.scripts } : {}),
        };
    }

    /**
     * Normalize the parsed document to exactly one root (SCENE_ROOT_SPEC §3) —
     * the one read-boundary migration. Exposed for subclasses whose
     * `parseSceneData` unpacks a container and cannot call the base
     * implementation.
     */
    protected migrateDocumentRoot(
        document: NormalizedSceneDocument,
        nodes: ParsedDocumentNode[],
        fileName?: string,
    ): DocumentRootMigration {
        return migrateToDocumentRoot(document, nodes, {
            ...(fileName ? { fileName } : {}),
            openAsScene: this._openAsScene,
        });
    }

    /**
     * Carry the document's own half onto a loaded result: the scripts map and
     * the foreign payload the next save must write back (issue #834).
     */
    protected applyDocumentData<TResult extends {
        nodes: ReadonlyArray<{ name: string }>;
        documentRoot?: unknown;
        scripts?: Record<string, string>;
        foreignDocument?: ForeignDocumentData;
    }>(
        result: TResult,
        structure: ParsedDocumentStructure,
    ): TResult {
        if (structure.scripts && Object.keys(structure.scripts).length > 0) {
            result.scripts = structure.scripts;
        }
        // The document root by IDENTITY: the loaded array is built in parsed
        // order, so the index the parsed root sits at is the loaded node's.
        if (structure.documentRoot) {
            const index = structure.nodes.indexOf(structure.documentRoot);
            if (index >= 0) result.documentRoot = result.nodes[index];
        }
        const foreign = foreignDocumentData(structure.document);
        if (foreign) result.foreignDocument = foreign;
        return result;
    }

    /**
     * If the document carries a document-level `KHR_interactivity` extension,
     * convert it back to a BehaviorGraph and put it back on the node it was
     * promoted from: the **document root** (SCENE_ROOT_SPEC §2.2 step 5, D8).
     *
     * One destination, whatever the document's provenance — the legacy
     * synthetic `"Scene"` node, a pre-D8 document whose root the migration
     * just folded, or a D8 document's own root — because by the time this
     * runs they are all the same thing: `migration.root`. A foreign multi-root
     * document that was merely placed has no document root, and the graph goes
     * where it has always gone, on the first root node.
     *
     * Skips when the root already has an authored BehaviorGraph, to avoid
     * duplicating one that was never promoted.
     */
    protected restoreKHRInteractivity(
        document: NormalizedSceneDocument,
        nodes: ParsedDocumentNode[],
        migration: DocumentRootMigration,
    ): void {
        const khrData = document.extensions?.['KHR_interactivity'] as KHRInteractivityData | undefined;
        if (!khrData?.graphs?.length) return;

        const rootIndices = document.scenes[document.scene]?.nodes ?? document.scenes[0]?.nodes ?? [];
        const rootNode = migration.root ?? nodes[rootIndices[0]];
        if (!rootNode) return;

        const rawExtras = (rootNode.extras as Record<string, unknown> | undefined) ?? {};
        const existingTrace = (rawExtras.trace as { behaviors?: Array<{ '@type': string }> } | undefined) ?? {};
        const existingBehaviors = existingTrace.behaviors ?? [];
        if (existingBehaviors.some(b => b['@type'] === 'BehaviorGraph')) return;

        // Build a translation context so spec-mandated `nodeIndex` / pointer
        // configs come back as authoring-friendly TransformPath strings,
        // relative to the host where possible.
        const documentNodes = document.nodes;
        const nameMap = new Map<number, string>();
        const parentMap = new Map<number, number>();
        for (let i = 0; i < documentNodes.length; i++) {
            nameMap.set(i, documentNodes[i].name);
            const children = documentNodes[i].children;
            if (children) {
                for (const c of children) parentMap.set(c, i);
            }
        }

        // The host is the document root's node index. A root the READER
        // synthesized (§3: a pre-D8 document with ≠ 1 root, a foreign one
        // opened as the scene) is not in `nodes[]` and has none, so it is given
        // the first index past the end — never a real node's, and made the
        // parent of every document root so a target under it still translates
        // to `./Child` instead of falling back to an absolute path.
        const hostIndex = migration.rootIndex ?? documentNodes.length;
        if (migration.rootIndex === undefined) {
            for (const rootIndex of rootIndices) {
                if (!parentMap.has(rootIndex)) parentMap.set(rootIndex, hostIndex);
            }
        }
        const importCtx: ImportTranslationContext = {
            hostIndex,
            nodeCount: documentNodes.length,
            nameOf: (idx: number) => (idx === hostIndex ? rootNode.name : nameMap.get(idx)),
            parentOf: (idx: number) => parentMap.get(idx),
        };

        const graphData = convertKHRInteractivityToGraphData(khrData, importCtx);
        if (graphData.nodes.length === 0) return;

        // A graph exported through the flatten-for-conformance pass arrives as
        // standard ops with re-collapse tags in `extras`; put the author's
        // subgraph boxes back. Returns null when there is nothing to recover
        // (or nothing that could be), in which case the flat graph stands.
        const recollapsed = recollapseSubgraphs(graphData);
        const bgBehavior: BehaviorGraphData = recollapsed
            ? { '@type': 'BehaviorGraph', graphs: recollapsed.graphs, activeGraph: recollapsed.activeGraph }
            : { '@type': 'BehaviorGraph', graph: graphData };

        rootNode.extras = {
            ...rawExtras,
            trace: { ...existingTrace, behaviors: [...existingBehaviors, bgBehavior] },
        };
    }

    /**
     * Read the document from whatever the loader pipeline supplies (JSON text,
     * a parsed object, a UTF-8 buffer, or a GLB container) and normalize the
     * legacy glXF and glTF 2.1 shapes into one.
     */
    protected parseDocument(data: unknown): NormalizedSceneDocument {
        const json = parseSceneDocumentJson(data);
        if (!json) {
            throw new Error(`${this.name}: scene document is not valid JSON`);
        }
        return normalizeSceneDocument(json);
    }

    /**
     * Parse a normalized document into the node array.
     */
    protected parseDocumentNodes(
        document: NormalizedSceneDocument,
        rootUrl: string,
        assetBlobUrls?: Map<string, string>
    ): ParsedDocumentNode[] {
        if (document.nodes.length === 0) {
            return [];
        }

        // Build node name map for parent references
        const nodeNames = new Map<number, string>();
        for (let i = 0; i < document.nodes.length; i++) {
            nodeNames.set(i, document.nodes[i].name);
        }

        // Build parent map from children arrays
        const parentMap = new Map<number, number>();
        for (let i = 0; i < document.nodes.length; i++) {
            const children = document.nodes[i].children;
            if (children) {
                for (const childIndex of children) {
                    parentMap.set(childIndex, i);
                }
            }
        }

        const nodes: ParsedDocumentNode[] = [];
        for (let i = 0; i < document.nodes.length; i++) {
            nodes.push(this.convertNode(document.nodes[i], i, document, nodeNames, parentMap, rootUrl, assetBlobUrls));
        }
        // Names are not unique (three "Frame"s in one display): link each node
        // to its parent by index, so the hierarchy never goes by name.
        for (const [childIndex, parentIndex] of parentMap) {
            if (nodes[childIndex] && nodes[parentIndex]) nodes[childIndex].parentNode = nodes[parentIndex];
        }

        return nodes;
    }

    /**
     * Convert a single document node to a parsed node.
     */
    protected convertNode(
        documentNode: NormalizedNode,
        nodeIndex: number,
        document: NormalizedSceneDocument,
        nodeNames: Map<number, string>,
        parentMap: Map<number, number>,
        rootUrl: string,
        assetBlobUrls?: Map<string, string>
    ): ParsedDocumentNode {
        const name = documentNode.name || `Node_${nodeIndex}`;

        // Build transform
        const transform = {
            translation: documentNode.translation as [number, number, number] | undefined,
            rotation: documentNode.rotation as [number, number, number, number] | undefined,
            scale: documentNode.scale as [number, number, number] | undefined,
        };

        // Get parent name
        const parentIndex = parentMap.get(nodeIndex);
        const parentName = parentIndex !== undefined ? nodeNames.get(parentIndex) : undefined;

        const file = documentNode.file !== undefined ? document.files[documentNode.file] : undefined;
        const uri = file?.uri;

        // Whatever this node carries that Trace did not author, kept for the
        // next save (issue #834).
        const foreign = foreignNodeData(documentNode);

        const parsed: ParsedDocumentNode = {
            name,
            // 2.1 node identity, carried so a load → save round trip preserves
            // it byte for byte (issue #837).
            ...(documentNode.uid ? { uid: documentNode.uid } : {}),
            originalAssetUri: uri,
            ...(file?.name ? { srcName: file.name } : {}),
            transform: (transform.translation || transform.rotation || transform.scale)
                ? transform
                : undefined,
            parentName,
            extras: documentNode.extras,
            ...(foreign ? { foreign } : {}),
        };

        // TRACE_animation_binding: each source file resolves exactly like a
        // node's own — kept verbatim as `src`, with the absolute URL a
        // folder-relative one names. Nothing is loaded here: a source is read
        // for its animations once the target is mounted
        // (`loaders/animationBinding`), and never instantiated.
        if (documentNode.animationBinding) {
            // A source whose file is missing is dropped, not fatal — like a
            // node whose own file is missing.
            const sources = documentNode.animationBinding.sources.flatMap(source => {
                const sourceUri = document.files[source.file]?.uri;
                if (sourceUri === undefined) {
                    Logger.warn(`[GltfScene] ${name}: TRACE_animation_binding source file ${source.file} not found; source dropped`);
                    return [];
                }
                const resolved = this.resolveFileUri(sourceUri, rootUrl, assetBlobUrls);
                return [{
                    src: sourceUri,
                    ...(resolved.folderUrl ? { folderUrl: resolved.folderUrl } : {}),
                    ...(source.root !== undefined ? { root: source.root } : {}),
                }];
            });
            if (sources.length > 0) parsed.animationBinding = { sources };
        }

        if (!file || !uri) return parsed;

        const resolution = this.resolveFileUri(uri, rootUrl, assetBlobUrls);
        parsed.resolvedSrcUrl = resolution.folderUrl;

        // Scene-format references (.trace/.tracex/… archives, and glTF
        // *documents* reached through a library reference) are materialized by
        // the Trace runtime via the nested-scene path (`SceneObject.src`), not
        // by the inline GLB loader — which would download the whole file only
        // to fail GLB parsing, and then the runtime would download it AGAIN.
        // The archive check runs on the verbatim URI: resolved URIs may be
        // extension-less blob URLs for ZIP-bundled assets.
        const isArchiveRef = isSceneFormatRef(uri);
        // A 2.1 external asset declared `model/gltf+json` is another document
        // (spec §3.4 — every library reference is a plain `externalAsset`).
        // Legacy glXF has no manifest and never referenced documents, so it
        // keeps its inline-load behaviour exactly.
        const isDocumentRef = document.sourceFormat === 'gltf21'
            && documentNode.carrier === 'externalAsset'
            && file.mimeType === GLTF_JSON_MIME;
        // **A library reference is classified by what the redirect RESOLVED
        // to, never by the entry (spec §3.1, §3.4).** `…/assets/{guid}/file`
        // answers 302 to whatever the row points at *now*: a leaf asset
        // answers with its own GLB, PNG or MP4, and the same asset after its
        // first edit answers with a document. Both are legal at the same
        // `files[k]`, and the entry's `mimeType` is the constant
        // `model/gltf+json` our writer stamps on EVERY reference (spec §3.4)
        // — a sentinel meaning "resolve through the reference path", not a
        // content type — so nothing here may decide on it. The reference
        // therefore goes down the nested path, which fetches it once and
        // decides on the response
        // (`TraceScene._loadFromURL`); inline-loading it would hand an
        // extension-less endpoint to the GLB parser and fail the moment the
        // library asset gains a document.
        const isLibraryRef = document.sourceFormat === 'gltf21'
            && matchAssetFileUrlPath(uri) !== null;
        parsed.sceneRef = isArchiveRef || isDocumentRef || isLibraryRef;

        // When a Trace experience node already carries an asset-owning
        // behavior (Model/Image/Video) in its extras, the Trace runtime will
        // load and parent the asset itself. Skipping the inline load here
        // avoids double-loading the same asset (once by this plugin, once by
        // the behavior). `originalAssetUri` is preserved so `SceneObject.src`
        // still round-trips for re-export.
        //
        // A bake whose `Provenance.bakedFrom` names another src is Trace's
        // too. An authoring load swaps it for that rig before the node is
        // built (`authoringSrc`) and builds the rig, so a bake loaded here
        // would sit under it as a second body: on reopen every Bevel'd card
        // drew its default-grey primitive over the coloured bake. Left to the
        // runtime, a play load keeps the bake as `src` and its implied Model
        // loads it once; the plugin need not know which load this is.
        const traceOwnsAsset = nodeHasAssetOwningBehavior(documentNode.extras)
            || bakedFromRig(uri, traceBehaviorsOf(documentNode.extras)) !== undefined;
        // The Trace carrier exists precisely for the files 2.1 forbids as
        // external assets — images, video, audio, nested scenes (plan D3).
        // None of them is a GLB, so none is ever inline-loaded.
        const inlineLoadable = documentNode.carrier === 'externalAsset'
            && (document.sourceFormat === 'glxf' || file.mimeType === GLTF_BINARY_MIME || file.mimeType === '');

        if (inlineLoadable && !traceOwnsAsset && !parsed.sceneRef) {
            parsed.assetUri = resolution.url;
        }

        return parsed;
    }

    /**
     * Resolve a `files[k].uri` to something loadable.
     *
     * Order: bundled ZIP blob → absolute pass-through → sibling blob →
     * **the document's own URL**. Relative URIs are folder-relative and the
     * folder is wherever the document was fetched from (spec §2.1/§2.3), so
     * `rootUrl` — the *resolved* response URL after any redirect — is the base.
     *
     * `folderUrl` is set only for that last case: it is the absolute URL an
     * authored relative URI names, which a behavior that owns the asset needs
     * even though its `src` stays relative.
     */
    protected resolveFileUri(
        uri: string,
        rootUrl: string,
        assetBlobUrls?: Map<string, string>
    ): { url: string; folderUrl?: string } {
        // Try blob URL first (for ZIP-based formats)
        if (assetBlobUrls) {
            const filename = this.extractFilename(uri);
            let blobUrl = assetBlobUrls.get(filename);

            if (!blobUrl && uri.startsWith('assets/')) {
                const cleanUri = uri.replace(/^assets\//, '');
                blobUrl = assetBlobUrls.get(cleanUri);
            }

            if (blobUrl) {
                return { url: blobUrl };
            }
        }

        // Absolute references pass through untouched
        if (isAbsoluteUri(uri)) {
            return { url: uri };
        }

        // Sibling blobs registered alongside a single-file open. The lookup
        // owns the key-form permutations (assets/ prefix, basename fallback)
        // — see ResourceManager.resolveExternalAssetBlob.
        if (this._externalAssetLookup) {
            const blob = this._externalAssetLookup(uri);
            if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                this.trackBlobUrl(blobUrl);
                return { url: blobUrl };
            }
        }

        // Resolve relative to the document's own URL.
        const folderUrl = resolveAgainst(uri, rootUrl);
        return { url: folderUrl, folderUrl: folderUrl === uri ? undefined : folderUrl };
    }

    /**
     * Extract filename from a path
     */
    protected extractFilename(path: string): string {
        const parts = path.replace(/\\/g, '/').split('/');
        return parts[parts.length - 1] || path;
    }

    // =========================================================================
    // Loading Methods
    // =========================================================================

    /**
     * Load all nodes into the scene and return the loaded result
     */
    protected async loadNodesWithResult(
        structure: ParsedDocumentStructure,
        scene: Scene,
        onProgress?: (event: ISceneLoaderProgressEvent) => void
    ): Promise<GLXFLoadedResult> {
        const result = await loadNodesWithResultUtil({
            nodes: structure.nodes,
            scene,
            onProgress,
            loadAsset: (uri, parent) => this.loadGLBAsset(uri, parent, scene),
            extractMetadata: (parsed, loaded) => this.extractNodeMetadata(parsed, loaded),
        });

        // Say, per node, that THIS plugin owns the file it just put in the
        // scene. Without it the runtime's "src as template" rule reads the
        // same `src` and adds a `Model` that loads the GLB a second time —
        // two co-located copies of the geometry, twice the draw calls
        // (CONTENT_MODEL_SPEC §3.1: the server's wrapper document authors no
        // behaviors, so nothing else suppressed the synthesis).
        //
        // Only for a load that SUCCEEDED. A failed one instantiated nothing,
        // and the identity behavior still matters there — see the
        // ASSET_PRIMITIVE_TYPES branch in `SceneCreation.applyComponents`.
        //
        // Stated here rather than in the shared util because "the plugin
        // inline-loads" is this plugin's contract: `.tracex` reuses the same
        // util with a no-op loader precisely so its behaviors own the assets.
        for (const node of result.nodes) {
            if (node.assetUri && !node.loadError) node.assetInstantiated = true;
        }

        return this.applyDocumentData(result, structure);
    }

    /**
     * Extract metadata from parsed node extras into the loaded node.
     *
     * Forwards the verbatim `files[k].uri` into `loaded.src` for round-trip,
     * plus any Trace payload under `extras.trace.{behaviors,active}` so a
     * standalone document reaches `loadFromPluginResult` carrying its
     * behaviors. Subclasses may overwrite `src` if their format carries a
     * richer original path (e.g. bundled `./assets/foo.glb`).
     */
    protected extractNodeMetadata(
        parsedNode: ParsedDocumentNode,
        loadedNode: GLXFLoadedNode
    ): void {
        if (parsedNode.originalAssetUri) {
            loadedNode.src = parsedNode.originalAssetUri;
        }
        if (parsedNode.resolvedSrcUrl) {
            loadedNode.srcUrl = parsedNode.resolvedSrcUrl;
        }
        if (parsedNode.srcName) {
            loadedNode.srcName = parsedNode.srcName;
        }
        if (parsedNode.sceneRef) {
            loadedNode.sceneRef = true;
        }
        if (parsedNode.uid) {
            loadedNode.uid = parsedNode.uid;
        }
        if (parsedNode.foreign) {
            loadedNode.foreign = parsedNode.foreign;
        }
        if (parsedNode.animationBinding) {
            const sources = parsedNode.animationBinding.sources;
            loadedNode.animationBinding = { sources: sources.map(({ src, root }) => ({ src, ...(root !== undefined ? { root } : {}) })) };
            const urls = sources.flatMap(({ src, folderUrl }) => (folderUrl ? [[src, folderUrl] as [string, string]] : []));
            if (urls.length > 0) loadedNode.animationSourceUrls = urls;
        }
        const traceExtras = (parsedNode.extras as { trace?: { behaviors?: unknown[]; active?: boolean | string } } | undefined)?.trace;
        if (traceExtras) {
            if (traceExtras.behaviors) {
                loadedNode.behaviors = traceExtras.behaviors;
            }
            if (traceExtras.active !== undefined) {
                loadedNode.active = traceExtras.active;
            }
        }
    }

    /**
     * Load nodes into an asset container
     */
    protected async loadNodesIntoContainer(
        structure: ParsedDocumentStructure,
        container: AssetContainer,
        scene: Scene,
        onProgress?: (event: ISceneLoaderProgressEvent) => void
    ): Promise<void> {
        return loadNodesIntoContainerUtil({
            nodes: structure.nodes,
            container,
            scene,
            onProgress,
            loadAsset: (uri, parent) => this.loadGLBAsset(uri, parent, scene),
        });
    }

    /**
     * Load a single GLB asset
     */
    protected async loadGLBAsset(
        assetUrl: string,
        parent: TransformNode,
        scene: Scene
    ): Promise<ISceneLoaderAsyncResult & { extensionsUsed?: string[] }> {
        let extensionsUsed: string[] | undefined;

        const result = await ImportMeshAsync(assetUrl, scene, {
            pluginExtension: ".glb",
            pluginOptions: {
                gltf: {
                    // Nothing plays until the runtime says so. Babylon's
                    // default starts the file's first clip, looping, the
                    // moment it loads — before any behavior is attached, in
                    // edit mode too. The runtime owns playback instead: an
                    // `Animation` behavior plays per its data (a recording's
                    // performance waits for its Timeline, RECORDINGS_SPEC
                    // §7.1), and a file with no Animation behavior loops its
                    // first clip in play mode only, the same as a file the
                    // Model behavior loads (`TraceScene._updateUnmanagedAnimations`).
                    animationStartMode: GLTFLoaderAnimationStartMode.NONE,
                    onParsed: (loaderData: IGLTFLoaderData) => {
                        extensionsUsed = (loaderData.json as Record<string, unknown>).extensionsUsed as string[] | undefined;
                    }
                }
            }
        });

        if (result.meshes.length > 0) {
            const rootMesh = result.meshes[0];
            this.processRootNode(rootMesh, parent);
        }

        return Object.assign(result, { extensionsUsed });
    }

    /**
     * Handle Babylon's __root__ container node.
     */
    protected processRootNode(rootMesh: AbstractMesh, parent: TransformNode): TransformNode {
        return processRootNode(rootMesh, parent);
    }

    /**
     * Apply transform data to a TransformNode
     */
    protected applyTransform(
        node: TransformNode,
        transform?: ParsedDocumentNode['transform']
    ): void {
        applyTransform(node, transform);
    }

    // =========================================================================
    // Blob URL Management
    // =========================================================================

    /**
     * Track a blob URL for later cleanup
     */
    protected trackBlobUrl(url: string): void {
        this._blobUrls.push(url);
    }

    /**
     * Cleanup all tracked blob URLs
     */
    protected cleanupBlobUrls(): void {
        for (const url of this._blobUrls) {
            URL.revokeObjectURL(url);
        }
        this._blobUrls = [];
    }
}

/**
 * Standard relative-URI resolution against the document's URL, falling back to
 * concatenation when `rootUrl` is not a usable base (empty, or a bare
 * directory string as tests and local opens supply).
 */
export function resolveAgainst(uri: string, rootUrl: string): string {
    if (!rootUrl) return uri;
    try {
        return new URL(uri, rootUrl).href;
    } catch {
        return rootUrl + uri;
    }
}
